import axios from "axios";
import { loadingAction } from "ReduxReducers/loadingReducer";
import { IdleAction } from "ReduxReducers/tokenGenerateTime";
import { appConstants } from "./appConstants";
import { showResMsg } from "./generic";
import { renderDocumentContent } from "./documentPdfContent";

const documents = {
  quotation: {
    type: "quotation",
    idField: "quotationID",
    dataEndpoint: "quotation/previewQuotationDetails",
    selector: ".customer-invoice-preview-card",
    endpoint: "quotation/downloadQuotation",
  },
  invoice: {
    type: "invoice",
    idField: "invoiceID",
    dataEndpoint: "invoice/previewInvoiceDetails",
    selector: ".customer-invoice-preview-card",
    endpoint: "invoicePdfFormat/downloadInvoice",
  },
  rcti: {
    type: "rcti",
    idField: "rctiID",
    dataEndpoint: "invoice/viewRCTIByID",
    selector: ".rcti-detail-document",
    endpoint: "invoice/rctiDownload",
  },
};

// Capture the rendered customer document, including its computed website styles.
// Navigation, action buttons, private history and application chrome are excluded.
const styleProperties = [
  "display",
  "box-sizing",
  "width",
  "min-width",
  "max-width",
  "padding",
  "margin",
  "border",
  "border-radius",
  "background-color",
  "color",
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "line-height",
  "letter-spacing",
  "text-align",
  "text-transform",
  "white-space",
  "overflow-wrap",
  "vertical-align",
  "flex",
  "flex-direction",
  "flex-wrap",
  "align-items",
  "justify-content",
  "gap",
  "grid-template-columns",
  "grid-column",
  "grid-row",
  "border-collapse",
  "table-layout",
  "box-shadow",
  "fill",
  "stroke",
  "stroke-width",
];

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result), { once: true });
    reader.addEventListener("error", reject, { once: true });
    reader.readAsDataURL(blob);
  });

async function inlineSnapshotImages(originals, copies) {
  await Promise.all(
    originals.map(async (node, index) => {
      if (node.tagName !== "IMG") return;
      const source = node.currentSrc || node.getAttribute("src") || "";
      if (!source || source.startsWith("data:")) return;
      try {
        const response = await fetch(source, {
          credentials: "omit",
          mode: "cors",
        });
        if (!response.ok) throw new Error("Image could not be loaded.");
        copies[index].setAttribute(
          "src",
          await blobToDataUrl(await response.blob())
        );
      } catch {
        // Do not leave an external URL in the self-contained PDF snapshot.
        copies[index].removeAttribute("src");
      }
    })
  );
}

async function snapshotDocument(element) {
  const view = element.ownerDocument.defaultView;
  const clone = element.cloneNode(true);
  const originals = [element, ...element.querySelectorAll("*")];
  const copies = [clone, ...clone.querySelectorAll("*")];
  const styles = new Map();
  originals.forEach((node, index) => {
    const computed = view.getComputedStyle(node);
    const declarations =
      styleProperties
        .map((property) => `${property}:${computed.getPropertyValue(property)}`)
        .join(";") +
      (node.matches(
        "tr, .invoice-detail-items .row, .preview-totals-column, .quotation-preview-totals"
      )
        ? ";break-inside:avoid"
        : "");
    if (!styles.has(declarations))
      styles.set(declarations, `pdf-style-${styles.size}`);
    copies[index].setAttribute("class", styles.get(declarations));
    copies[index].removeAttribute("style");
    [...copies[index].attributes].forEach((attribute) => {
      if (/^on/i.test(attribute.name))
        copies[index].removeAttribute(attribute.name);
    });
  });
  await inlineSnapshotImages(originals, copies);
  clone
    .querySelectorAll(
      "script, iframe, object, embed, button, input, textarea, select"
    )
    .forEach((node) => node.remove());
  const css = [...styles]
    .map(([declarations, name]) => `.${name}{${declarations}}`)
    .join("\n");
  const measuredWidth = Math.ceil(element.getBoundingClientRect().width);
  const fallbackWidth = Math.ceil(element.scrollWidth || 1024);
  const snapshotWidth = Math.min(
    4096,
    Math.max(1, Number.isFinite(measuredWidth) && measuredWidth > 0
      ? measuredWidth
      : fallbackWidth)
  );
  return {
    html: clone.outerHTML,
    css,
    width: snapshotWidth,
  };
}

async function waitForDocumentFonts(element) {
  const doc = element.ownerDocument;
  if (doc.fonts.status === "loaded") return;
  const fonts = new Set(
    [element, ...element.querySelectorAll("*")].map((node) => {
      const style = doc.defaultView.getComputedStyle(node);
      return `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    })
  );
  let timer;
  try {
    await Promise.race([
      Promise.all(
        [...fonts].map((font) => doc.fonts.load(font).catch(() => []))
      ),
      new Promise((resolve) => {
        timer = window.setTimeout(resolve, 2000);
      }),
    ]);
  } finally {
    window.clearTimeout(timer);
  }
}

async function waitForDocumentImages(element) {
  const pending = [...element.querySelectorAll("img")]
    .filter((image) => !image.complete)
    .map(
      (image) =>
        new Promise((resolve) => {
          image.addEventListener("load", resolve, { once: true });
          image.addEventListener("error", resolve, { once: true });
        })
    );
  if (!pending.length) return;
  let timer;
  try {
    await Promise.race([
      Promise.all(pending),
      new Promise((resolve) => {
        timer = window.setTimeout(resolve, 3000);
      }),
    ]);
  } finally {
    window.clearTimeout(timer);
  }
}

async function captureDocument(config, id) {
  const currentDocument = document.querySelector(config.selector);
  if (
    currentDocument?.dataset.pdfReady === "true" &&
    currentDocument.closest("[data-pdf-document-id]")?.dataset.pdfDocumentId ===
      String(id)
  ) {
    await waitForDocumentFonts(currentDocument);
    await waitForDocumentImages(currentDocument);
    return snapshotDocument(currentDocument);
  }
  let login = {};
  try {
    login = JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "{}");
  } catch {
    /* Use the document's business details. */
  }
  const requestConfig = {
    headers: { Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}` },
    timeout: 15000,
  };
  const dataRequest = axios.post(
    `${appConstants.baseURL}${config.dataEndpoint}`,
    { [config.idField]: id, businessUserID: login?.businessUserID },
    requestConfig
  );
  const currencyRequest =
    config.type === "invoice"
      ? axios
          .post(
            `${appConstants.baseURL}defaultSettings/fetchDefaultSettingCurrency`,
            { businessUserID: login?.businessUserID },
            requestConfig
          )
          .catch(() => null)
      : Promise.resolve(null);
  const userId = login?._id || sessionStorage.getItem("USER_ID")?.replace(/["']/g, "");
  const profileRequest = userId
    ? axios
        .post(
          `${appConstants.baseURL}user/fetchUserProfile`,
          { _id: userId },
          requestConfig
        )
        .catch(() => null)
    : Promise.resolve(null);
  const [response, currencyResponse, profileResponse] = await Promise.all([
    dataRequest,
    currencyRequest,
    profileRequest,
  ]);
  const values = response.data?.Result_Output?.[0];
  if (!response.data?.Result_Status || !values)
    throw new Error(
      response.data?.Result_Message ||
        "The document could not be loaded for download."
    );
  if (currencyResponse?.data?.Result_Status)
    values.pdfCurrencyCode = currencyResponse.data.Result_Output;
  const profileOutput = profileResponse?.data?.Result_Output;
  const profile = Array.isArray(profileOutput) ? profileOutput[0] : profileOutput;
  if (profile) {
    values.businessCompanyName ||= profile.companyName;
    values.bankAccountName ||= profile.bankAccountName;
    values.bankBsbCode ||= profile.bankBsbCode;
    values.bankAccountNumber ||= profile.bankAccountNumber;
  }
  const container = document.createElement("div");
  if (config.type === "quotation") {
    container.setAttribute("data-document-theme", "print");
  }
  container.setAttribute("aria-hidden", "true");
  container.inert = true;
  container.style.cssText =
    "position:fixed;left:-20000px;top:0;width:1440px;pointer-events:none;";
  // Customer-facing PDFs use a stable light document theme regardless of the sender's app theme.
  const dark = false;
  container.innerHTML = renderDocumentContent(
    config.type,
    values,
    String(id),
    login || {},
    dark
  );
  document.body.appendChild(container);
  try {
    const element =
      container.querySelector(config.selector) ||
      (config.type === "quotation"
        ? container.querySelector(".quotation-preview-document")
        : null);
    if (!element) {
      throw new Error("The customer document could not be rendered for PDF.");
    }
    await waitForDocumentFonts(element);
    await waitForDocumentImages(element);
    return snapshotDocument(element);
  } finally {
    container.remove();
  }
}

export async function captureWebsiteDocument(type, id) {
  if (!id) throw new Error("Save the document before preparing its PDF.");
  return captureDocument(documents[type], id);
}

export async function downloadWebsiteDocument(
  type,
  values,
  callback,
  dispatch
) {
  dispatch(loadingAction(true));
  try {
    if (!values._id)
      throw new Error("Save the document before downloading its PDF.");
    const config = documents[type];
    const documentSnapshot = type === "rcti" ? undefined : await captureDocument(config, values._id);
    const response = await axios.post(
      `${appConstants.baseURL}${config.endpoint}`,
      { ...values, documentSnapshot },
      {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}` },
        timeout: 60000,
      }
    );
    await callback(response);
    dispatch(IdleAction(new Date()));
    return response;
  } catch (error) {
    showResMsg(
      error.response?.data?.Result_Message ||
        error.message ||
        "PDF could not be downloaded. Please try again.",
      2500,
      false
    );
    return null;
  } finally {
    dispatch(loadingAction(false));
  }
}

export function savePdfResponse(response, fileName) {
  const binary = atob(response.data);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  const url = URL.createObjectURL(
    new Blob([bytes], { type: "application/pdf" })
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName.replace(/[\\/:*?"<>|]/g, "_");
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
